// src/contexts/NotificationContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import oneSpaceApi from "../lib/onespace-api";
import chromeNotifications from '../lib/chrome-notifications';
import useNotificationPermission from '../hooks/useNotificationPermission';
import { 
  setupUserChannels, 
  leaveAllChannels, 
  getActiveChannels,
  NotificationData,
  NotificationHandler,
  EventHandler
} from '../lib/echo-channels';

// Generic action handler interface
interface ActionHandler {
  [actionName: string]: (data: any) => Promise<void> | void;
}

interface NotificationContextType {
  notifications: NotificationData[];
  unreadCount: number;
  fetchNotifications: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  spotlightNotification: NotificationData | null;
  showSpotlight: boolean;
  setShowSpotlight: (show: boolean) => void;
  // Add action registration
  registerActionHandler: (actionName: string, handler: (data: any) => Promise<void> | void) => void;
  unregisterActionHandler: (actionName: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [spotlightNotification, setSpotlightNotification] = useState<NotificationData | null>(null);
  const [showSpotlight, setShowSpotlight] = useState(false);
  const { isAuthenticated, user } = useAuth();
  const [echoInitialized, setEchoInitialized] = useState(false);
  const [activeChannels, setActiveChannels] = useState<string[]>([]);
  const { hasPermission, requestPermission } = useNotificationPermission();
  const [actionHandlers, setActionHandlers] = useState<ActionHandler>({});

  // Request notification permission when user is authenticated
  useEffect(() => {
    if (isAuthenticated && !hasPermission) {
      const timer = setTimeout(() => {
        requestPermission().then(permission => {
          if (permission === 'granted') {
            console.log('✅ Notification permission granted');
          } else {
            console.log('❌ Notification permission denied');
          }
        });
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, hasPermission, requestPermission]);

  // Built-in action handlers
  const builtInActionHandlers: ActionHandler = {
    refresh_settings: async (data: any) => {
      console.log('🔄 Executing refresh_settings action:', data);
      try {
        // Show visual feedback for settings refresh
        console.log('🔥 Broadcasting settings refresh across the application...');
        
        // Option 1: Force reload the page (similar to emptying body but cleaner)
        if (data.force_reload === true) {
          console.log('🔄 Force reloading page due to critical settings change...');
          window.location.reload();
          return;
        }
        
        // Option 2: Graceful refresh (default)
        // Fetch fresh settings from API
        const response = await oneSpaceApi.get('/api/settings/general');
        console.log('✅ Settings refreshed:', response.data);
        
        // Dispatch custom event that components can listen to
        window.dispatchEvent(new CustomEvent('settings_refreshed', {
          detail: response.data
        }));
        
        // Also dispatch the legacy event for backwards compatibility
        window.dispatchEvent(new CustomEvent('onespace-settings-refresh', {
          detail: {
            message: data.message || 'Settings have been updated',
            timestamp: data.time || new Date().toISOString(),
            tenantId: data.tenant_id
          }
        }));
        
        // Show success message
        console.log('✅ Settings have been refreshed due to remote update');
        
        // Optional: Show user notification
        if (data.message) {
          console.log(`📢 Settings update: ${data.message}`);
        }
      } catch (error) {
        console.error('❌ Error refreshing settings:', error);
      }
    },

    refresh_spotlights: async (data: any) => {
      console.log('🔦 Executing refresh_spotlights action:', data);
      try {
        // Show visual feedback for spotlight refresh
        console.log('🔥 Broadcasting spotlight refresh across the application...');
        
        // Dispatch custom event that SpotlightManager can listen to
        window.dispatchEvent(new CustomEvent('spotlight_refresh', {
          detail: {
            spotlight: data.spotlight || null,
            published_by: data.published_by || null,
            timestamp: data.timestamp || new Date().toISOString(),
            tenant_id: data.tenant_id,
            message: `New spotlight published: ${data.spotlight?.title || 'Unknown'}`
          }
        }));
        
        console.log('✅ Spotlight refresh event dispatched');
        
        // Optional: Show user notification about new spotlight
        if (data.spotlight && data.spotlight.title) {
          console.log(`📢 New spotlight available: ${data.spotlight.title}`);
        }
      } catch (error) {
        console.error('❌ Error refreshing spotlights:', error);
      }
    },
    
    refresh_timer: async (data: any) => {
      console.log('🔄 Executing refresh_timer action:', data);
      try {
        // Fetch fresh timer info from API
        const response = await oneSpaceApi.get('/api/timer');
        console.log('✅ Timer refreshed:', response.data);
        
        // Dispatch custom event that components can listen to
        window.dispatchEvent(new CustomEvent('timer_refreshed', {
          detail: response.data
        }));
        
        console.log('Timer info has been refreshed due to remote update');
      } catch (error) {
        console.error('❌ Error refreshing timer:', error);
      }
    },
    
    force_reload: async (data: any) => {
      console.log('🔄 Executing force_reload action:', data);
      console.log('🔄 Force reloading page due to critical change...');
      
      // Show user message before reload if provided
      if (data.message) {
        console.log(`📢 ${data.message}`);
        // Could show a toast notification here
      }
      
      // Small delay to allow logs to be shown
      setTimeout(() => {
        window.location.reload();
      }, 500);
    },
    
    refresh_page: async (data: any) => {
      console.log('🔄 Executing refresh_page action:', data);
      
      // More elegant than emptying body - just reload
      if (data.immediate === true) {
        window.location.reload();
      } else {
        // Give user a moment to see any messages
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    }
  };

  // Function to register custom action handlers
  const registerActionHandler = useCallback((actionName: string, handler: (data: any) => Promise<void> | void) => {
    setActionHandlers(prev => ({
      ...prev,
      [actionName]: handler
    }));
    console.log(`📝 Registered action handler: ${actionName}`);
  }, []);

  // Function to unregister action handlers
  const unregisterActionHandler = useCallback((actionName: string) => {
    setActionHandlers(prev => {
      const newHandlers = { ...prev };
      delete newHandlers[actionName];
      return newHandlers;
    });
    console.log(`🗑️ Unregistered action handler: ${actionName}`);
  }, []);

  // Execute action based on notification data
  const executeAction = useCallback(async (notification: NotificationData) => {
    const actionName = notification.data.action;
    if (!actionName) return;

    console.log(`🎯 Executing action: ${actionName}`, notification.data);

    // Check built-in handlers first
    if (builtInActionHandlers[actionName]) {
      try {
        await builtInActionHandlers[actionName](notification.data);
      } catch (error) {
        console.error(`❌ Error executing built-in action ${actionName}:`, error);
      }
      return;
    }

    // Check custom registered handlers
    if (actionHandlers[actionName]) {
      try {
        await actionHandlers[actionName](notification.data);
      } catch (error) {
        console.error(`❌ Error executing custom action ${actionName}:`, error);
      }
      return;
    }

    console.warn(`⚠️ Unknown action: ${actionName}`);
  }, [actionHandlers]);

  // Functie om initiele notificaties op te halen
  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated || !user) return;
    try {
      const response = await oneSpaceApi.get<{data: NotificationData[]}>('/api/notifications'); 
      
      if (response && response.data && Array.isArray(response.data.data)) {
        setNotifications(response.data.data);
        setUnreadCount(response.data.data.filter((n: NotificationData) => !n.read_at).length);
      } else {
        console.warn('Unexpected response format from notifications API:', response);
        setNotifications([]);
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Fout bij ophalen notificaties via oneSpaceApi:', error);
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [isAuthenticated, user]);

  // Central notification handler
  const handleIncomingNotification = useCallback(async (notification: NotificationData) => {
    console.log('📬 Notification received:', notification);
    
    // Execute action if present
    await executeAction(notification);
    
    // Send browser/Chrome notification for all notifications
    const title = notification.data.title || 'New Notification';
    const message = notification.data.message || '';
    const actionUrl = notification.data.action_url;
     
    chromeNotifications.showNotification({
      title,
      message,
      type: 'basic',
      priority: notification.spotlight || notification.data.spotlight ? 2 : 1,
      onClicked: actionUrl ? () => window.open(actionUrl, '_blank') : undefined
    }).then(notificationId => {
      if (notificationId) {
        console.log('Browser notification shown with ID:', notificationId);
      }
    }).catch(error => {
      console.error('Error showing browser notification:', error);
    });
    
    // Handle spotlight notifications
    if (notification.spotlight || notification.data.spotlight) {
      handleSpotlightNotification(notification);
    }
    
    // Add to notification list (only if it's a regular notification, not just an action)
    if (!notification.data.action || notification.data.message) {
      setNotifications(prevNotifications => [notification, ...prevNotifications]);
      setUnreadCount(prevCount => prevCount + 1);
    }
  }, [executeAction]);

  // Handle settings refresh events
  const handleSettingsRefresh: EventHandler = useCallback((data: any) => {
    console.log('⚙️ Settings refresh event received:', data);
    handleIncomingNotification({
      id: `settings_${Date.now()}`,
      type: 'SettingsRefreshed',
      data: {
        message: data.message || 'Settings have been updated',
        action: 'refresh_settings',
        ...data
      },
      read_at: null,
      created_at: new Date().toISOString()
    });
  }, [handleIncomingNotification]);

  // Handle spotlight published events
  const handleSpotlightPublished: EventHandler = useCallback((data: any) => {
    console.log('🔦 Spotlight published event received:', data);
    handleIncomingNotification({
      id: `spotlight_${Date.now()}`,
      type: 'SpotlightPublished',
      data: {
        message: `New spotlight published: ${data.spotlight?.title || 'Unknown'}`,
        action: 'refresh_spotlights',
        ...data
      },
      read_at: null,
      created_at: new Date().toISOString()
    });
  }, [handleIncomingNotification]);

  // Set up Echo channels using the new channel manager
  const setupEchoChannels = useCallback(async () => {
    if (!isAuthenticated || !user?.id) return;
    
    try {
      console.log('🔧 Setting up Echo channels through channel manager...');
      
      const channels = await setupUserChannels(
        user,
        handleIncomingNotification,
        handleSettingsRefresh
      );
      
      setActiveChannels(channels);
      setEchoInitialized(true);
      
      console.log(`✅ Echo channels setup complete: ${channels.length} channels active`);
      
    } catch (error) {
      console.error('❌ Error setting up Echo channels:', error);
    }
  }, [isAuthenticated, user, handleIncomingNotification, handleSettingsRefresh]);

  // Effect om initiele data te laden en te luisteren naar Echo
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      fetchNotifications();
      // Only setup Echo channels if not already initialized
      if (!echoInitialized) {
        setupEchoChannels();
      }
    } else {
      // Reset state als gebruiker niet ingelogd is
      setNotifications([]);
      setUnreadCount(0);
      setSpotlightNotification(null);
      setShowSpotlight(false);
      setEchoInitialized(false);
    }
    
    // Cleanup functie
    return () => {
      // Leave all channels using the channel manager
      console.log('🧹 Cleaning up notification channels...');
      leaveAllChannels();
      setActiveChannels([]);
      setEchoInitialized(false);
    };
  }, [isAuthenticated, user?.id, fetchNotifications]); // Removed setupEchoChannels from dependencies

  // Functie om een notificatie als gelezen te markeren
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await oneSpaceApi.post(`/api/notifications/${notificationId}/mark-as-read`);

      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId ? { ...n, read_at: new Date().toISOString() } : n
        )
      );
      
      if (spotlightNotification?.id === notificationId) {
        setSpotlightNotification(null);
        setShowSpotlight(false);
      }
      
      setUnreadCount(prev => Math.max(0, prev - 1)); 
    } catch (error) {
      console.error('Fout bij markeren als gelezen via oneSpaceApi:', error);
    }
  }, [spotlightNotification]);

  // Functie om alle notificaties als gelezen te markeren
  const markAllAsRead = useCallback(async () => {
    try {
      await oneSpaceApi.post('/api/notifications/mark-all-as-read');

      setNotifications(prev =>
        prev.map(n => (n.read_at ? n : { ...n, read_at: new Date().toISOString() }))
      );
      
      setSpotlightNotification(null);
      setShowSpotlight(false);
      setUnreadCount(0);
    } catch (error) {
      console.error('Fout bij markeren alles als gelezen via oneSpaceApi:', error);
    }
  }, []);

  // Handle spotlight notifications when received
  const handleSpotlightNotification = useCallback((notification: NotificationData) => {
    console.log('🔦 Spotlight notification received:', notification);
    
    setSpotlightNotification(notification);
    setShowSpotlight(true);
    
    if (chromeNotifications.isExtension) {
      const title = notification.data.title || 'Notification';
      const message = notification.data.message || '';
      const actionUrl = notification.data.action_url;
      
      chromeNotifications.showSpotlightNotification(title, message, actionUrl)
        .then(notificationId => {
          console.log('Chrome notification shown with ID:', notificationId);
        })
        .catch(error => {
          console.error('Error showing Chrome notification:', error);
        });
    }
  }, []);

  const value = {
    notifications,
    unreadCount,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    spotlightNotification,
    showSpotlight,
    setShowSpotlight,
    registerActionHandler,
    unregisterActionHandler,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

// Hook om de context te gebruiken
export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      'useNotifications must be used within a NotificationProvider'
    );
  }
  return context;
};